<ng-container *vsLet="canAdjust$ | async as canAdjust">
  <div *ngIf="warranty$ | async as warranty">
    <div
      *ngIf="warranty?.policy as policy"
      class="flex rounded-md cursor-pointer transition hover:bg-gray-50"
      (click)="navigateToPolicy(policy.id)"
    >
      <div class="flex flex-col p-2 w-full">
        <div class="flex gap-4 my-3">
          <p class="text-sm font-medium text-gray-700 min-w-[112px]">Policy Name</p>
          <p tooltipStyle="light" class="text-sm self-center truncate whitespace-nowrap" [vsTooltip]="policy.title">
            {{ policy.title }}
          </p>
        </div>

        <vs-divider></vs-divider>

        <div class="flex gap-4 my-3">
          <p class="text-sm font-medium text-gray-700 min-w-[112px]">Policy Number</p>
          <p class="text-sm self-center truncate whitespace-nowrap">{{ policy.policyNumber }}</p>
        </div>

        <vs-divider></vs-divider>

        <div class="flex justify-between gap-4 py-3 w-full max-h-11 overflow-hidden">
          <div class="flex gap-4" style="max-width: calc(100% - 56px)">
            <p class="text-sm font-medium text-gray-700 min-w-[112px]">Start Date</p>
            <p class="text-sm self-center truncate whitespace-nowrap">{{ warranty?.termStart | date }}</p>
          </div>

          <button
            *ngIf="canAdjust"
            #trigger="cdkOverlayOrigin"
            vs-icon-button
            cdkOverlayOrigin
            class="-mt-[10px]"
            appearance="clear"
            (click)="toggle(); $event.stopImmediatePropagation()"
            [vsTooltip]="'Updating the start date will automatically adjust the expiry date'"
            tooltipStyle="light"
          >
            <vs-icon name="edit-01" class="m-2 text-gray-600"></vs-icon>

            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="trigger"
              [cdkConnectedOverlayOpen]="overlayOpen"
              (overlayOutsideClick)="overlayOpen ? (overlayOpen = false) : ''"
            >
              <vs-card>
                <vs-date-picker
                  [clearSelectionButton]="false"
                  [value]="warranty?.termStart"
                  (selectionChanged)="updateTermStart(warranty, $any($event)); overlayOpen = false"
                ></vs-date-picker>
              </vs-card>
            </ng-template>
          </button>
        </div>

        <vs-divider></vs-divider>

        <div class="flex gap-4 my-3">
          <p class="text-sm font-medium text-gray-700 min-w-[112px]">Expiry Date</p>
          <p class="text-sm self-center truncate whitespace-nowrap">{{ warranty?.expiryDate | date }}</p>
        </div>

        <ng-container *ngIf="warranty?.distributor as distributor">
          <vs-divider></vs-divider>

          <div class="flex gap-4 my-3">
            <p class="text-sm font-medium text-gray-700 min-w-[112px]">Distributor</p>
            <p class="text-sm self-center truncate whitespace-nowrap">{{ distributor.name }}</p>
          </div>

          <ng-container *ngIf="distributor.programName as programName">
            <vs-divider></vs-divider>
            <div class="flex gap-4 my-3">
              <p class="text-sm font-medium text-gray-700 min-w-[112px]">Program</p>
              <p class="text-sm self-center truncate whitespace-nowrap">{{ programName }}</p>
            </div>
          </ng-container>
        </ng-container>

        <ng-container *ngIf="warranty?.referenceId as referenceId">
          <vs-divider></vs-divider>

          <div class="flex gap-4 my-3 items-center">
            <p class="text-sm font-medium text-gray-700 min-w-[112px]">Warranty Ref ID</p>

            <div class="flex items-center gap-2 min-w-0">
              <p class="text-sm truncate whitespace-nowrap leading-5" [vsTooltip]="referenceId" tooltipStyle="light">
                {{ referenceId }}
              </p>

              <button
                vs-icon-button
                size="sm"
                appearance="clear"
                class="shrink-0 p-0 h-5 w-5 -my-1"
                aria-label="Copy reference ID"
                (click)="copyReferenceId(referenceId, $event)"
                [vsTooltip]="'Copy to clipboard'"
                tooltipStyle="light"
              >
                <vs-icon name="copy-01" class="h-4 w-4 text-gray-600"></vs-icon>
              </button>
            </div>
          </div>
        </ng-container>

        <vs-dialog
          #termStartReasonDialog
          [config]="{ dialogContainerClass: 'max-w-[540px]', disableBackdropDismiss: termStartReasonSaving }"
          (closed)="handleTermStartDialogClosed()"
        >
          <vs-dialog-header
            [config]="{
              mode: 'simple',
              icon: 'info-circle',
              iconTheme: 'primary',
              title: 'Add Adjustment Reason',
              subtitle: 'Please provide a reason for changing the warranty start date. This is required for auditing.',
              hideCloseButton: true,
            }"
          >
          </vs-dialog-header>

          <div class="flex flex-col gap-4">
            <div
              *ngIf="termStartAuditContext as ctx"
              class="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-800"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <p class="font-medium">Previous start date</p>
                  <p>{{ ctx.previousTermStart ? (ctx.previousTermStart | date: 'mediumDate') : 'N/A' }}</p>
                </div>
                <div class="text-right">
                  <p class="font-medium">Updated start date</p>
                  <p>{{ ctx.updatedTermStart | date: 'mediumDate' }}</p>
                </div>
              </div>
            </div>

            <vs-input-group>
              <label vsLabel for="term-start-reason">Reason<span class="text-danger">*</span>&nbsp;</label>
              <textarea
                vsInput
                id="term-start-reason"
                rows="4"
                [formControl]="termStartReasonControl"
                placeholder="Explain why the start date was changed"
              ></textarea>
              <p
                *ngIf="termStartReasonControl.invalid && termStartReasonControl.touched"
                class="text-sm text-danger mt-1 block"
              >
                Reason is required
              </p>
            </vs-input-group>
          </div>

          <div slot="buttons" class="flex gap-2 mt-2">
            <button
              vs-button
              class="flex-grow"
              theme="primary"
              [disabled]="termStartReasonSaving"
              (click)="submitTermStartReason()"
            >
              <ng-container *ngIf="!termStartReasonSaving">Submit</ng-container>
              <ng-container *ngIf="termStartReasonSaving">Saving...</ng-container>
            </button>
          </div>
        </vs-dialog>
      </div>
    </div>
  </div>
</ng-container>
